import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { IAPIAdapter } from 'plugins/tag/plugin_data_service/ts/Application/adapters/abstract/IAPIAdapter';
import { AdapterId } from 'plugins/tag/plugin_data_service/ts/Application/model/enums/AdapterId';
import { PTVContentType } from 'plugins/tag/plugin_data_service/ts/Application/model/enums/PTVContentType';
import { INextItemEndpoint } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/INextItemEndpoint';
import { INextItemFetchParams } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/INextItemFetchParams';
import { IUECFetchParams } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IUECFetchParams';
import { IUserListEndpoint } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IUserListEndpoint';
import { IUpNextEndpoint } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IUpNextEndpoint';
import { IUserListMutationParams } from 'tag/ts/tag/interfaces/api/IUserListMutationParams';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { PLUTO_LEGACY_API_PATHS } from './PlutoLegacyApiPaths';
import { PLUTO_LEGACY_SERVERS } from './PlutoLegacyServers';

/**
 * @copyright © Paramount Streaming
 * @description Legacy Pluto API adapter. Raw 1:1 wrapper for legacy Pluto endpoints,
 * including UEC up-next and next-item recommendations.
 * Same contract as PlutoAdapter; endpoint base URLs and paths may differ.
 * Simple response parsing (.json()) only; feature layer handles interpretation.
 */
export class PlutoLegacyAdapter implements IAPIAdapter, IUpNextEndpoint, INextItemEndpoint, IUserListEndpoint {
	readonly id = AdapterId.PLUTO_LEGACY;
	readonly #logger: ILogger = LogService.create( { prefixes: [ 'SmartTag:', 'DataService:', 'PlutoLegacyAdapter' ] } );

	/**
	 * @description Dispatches to the appropriate fetch method based on content type.
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<unknown>} Parsed API response
	 */
	async fetchData( paramsVO: IParamsVO ): Promise<unknown> {
		switch ( paramsVO?.STITCHER?.type ) {
			case PTVContentType.CHANNEL:
				return this.fetchChannels( paramsVO );
			case PTVContentType.MOVIE:
				return this.fetchVodItems( paramsVO );
			case PTVContentType.EPISODE:
				return this.fetchVodEpisodes( paramsVO );
			default:
				throw new Error( 'unknown stitcher content type' );
		}
	}

	/**
	 * @description Fetches live channel data. Does not throw on non-2xx — legacy API
	 * returns parseable JSON bodies on error responses; status handling is left to the caller.
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<unknown>} Parsed channels API response
	 */
	async fetchChannels( paramsVO: IParamsVO ): Promise<unknown> {
		const url = this.#buildChannelsApiUrl( paramsVO );
		const response = await this.#makeRequest( url, paramsVO );
		return response.json() as unknown;
	}

	/**
	 * @description Fetches VOD movie items. Does not throw on non-2xx — legacy API
	 * returns parseable JSON bodies on error responses; status handling is left to the caller.
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<unknown>} Parsed VOD items API response
	 */
	async fetchVodItems( paramsVO: IParamsVO ): Promise<unknown> {
		const url = this.#buildVodItemsApiUrl( paramsVO );
		const response = await this.#makeRequest( url, paramsVO );
		return response.json() as unknown;
	}

	/**
	 * @description Fetches VOD episode data. Does not throw on non-2xx — legacy API
	 * returns parseable JSON bodies on error responses; status handling is left to the caller.
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<unknown>} Parsed VOD episodes API response
	 */
	async fetchVodEpisodes( paramsVO: IParamsVO ): Promise<unknown> {
		const url = this.#buildVodEpisodesApiUrl( paramsVO );
		const response = await this.#makeRequest( url, paramsVO );
		return response.json() as unknown;
	}

	#getChannelsServer( paramsVO: IParamsVO ): string {
		return paramsVO?.STITCHER?.servers?.channels ?? PLUTO_LEGACY_SERVERS.channels;
	}

	#getVodServer( paramsVO: IParamsVO ): string {
		return paramsVO?.STITCHER?.servers?.vod ?? PLUTO_LEGACY_SERVERS.vod;
	}

	#getUecServer( paramsVO?: IParamsVO ): string {
		return ( paramsVO?.STITCHER?.servers?.uec ?? PLUTO_LEGACY_SERVERS.uec ).replace( /\/$/, '' );
	}

	#getNextItemServer( paramsVO?: IParamsVO ): string {
		return ( paramsVO?.STITCHER?.servers?.nextItem ?? PLUTO_LEGACY_SERVERS.nextItem ).replace( /\/$/, '' );
	}

	#buildChannelsApiUrl( paramsVO: IParamsVO ): string {
		const baseUrl = this.#getChannelsServer( paramsVO );
		const params = paramsVO?.STITCHER?.stitcherParams ?? '';
		const id = paramsVO?.STITCHER?.id;
		return `${baseUrl}${PLUTO_LEGACY_API_PATHS.channels}?${params}&channelIds=${id}`;
	}

	#buildVodItemsApiUrl( paramsVO: IParamsVO ): string {
		const baseUrl = this.#getVodServer( paramsVO );
		const params = paramsVO?.STITCHER?.stitcherParams ?? '';
		const id = paramsVO?.STITCHER?.id;
		return `${baseUrl}${PLUTO_LEGACY_API_PATHS.vodItems}?${params}&ids=${id}`;
	}

	#buildVodEpisodesApiUrl( paramsVO: IParamsVO ): string {
		const baseUrl = this.#getVodServer( paramsVO );
		const params = paramsVO?.STITCHER?.stitcherParams ?? '';
		const id = paramsVO?.STITCHER?.id;
		return `${baseUrl}${PLUTO_LEGACY_API_PATHS.vodEpisodes}?${params}&ids=${id}`;
	}

	async fetchUpNextRecommendations( params: IUECFetchParams, paramsVO?: IParamsVO ): Promise<unknown> {
		const url = this.#buildUpNextApiUrl( params, paramsVO );
		this.#logger.log( '[UEC]', 'fetchUpNextRecommendations', { url, params } );
		try {
			const response = await fetch( url, { method: 'GET' } );
			if ( !response.ok ) {
				this.#logger.warn( '[UEC]', 'up-next request failed', { status: response.status, url } );
				throw new Error( `UEC up-next request failed: ${response.status}` );
			}
			return response.json() as unknown;
		} catch ( error ) {
			if ( error instanceof Error && error.message.startsWith( 'UEC up-next request failed' ) ) {
				throw error;
			}
			this.#logger.warn( '[UEC]', 'fetchUpNextRecommendations error', {
				error: ( error as Error )?.message ?? error,
				url
			} );
			throw error;
		}
	}

	#buildUpNextApiUrl( params: IUECFetchParams, paramsVO?: IParamsVO ): string {
		const baseUrl = this.#getUecServer( paramsVO );
		const searchParams = new URLSearchParams();

		searchParams.set( 'endCardId', params.endCardId );
		searchParams.set( 'contentType', params.contentType );

		if ( params._clientRegion ) {
			searchParams.set( '_clientRegion', params._clientRegion );
		}
		if ( params.channelSlug ) {
			searchParams.set( 'channelSlug', params.channelSlug );
		}
		if ( params.device ) {
			searchParams.set( 'device', params.device );
		}
		if ( params.start !== undefined ) {
			searchParams.set( 'start', String( params.start ) );
		}
		if ( params.rows !== undefined ) {
			searchParams.set( 'rows', String( params.rows ) );
		}
		if ( params.nextItem === true ) {
			searchParams.set( 'nextItem', 'true' );
		}

		return `${baseUrl}${PLUTO_LEGACY_API_PATHS.upNext}?${searchParams.toString()}`;
	}

	async fetchNextItemRecommendations( params: INextItemFetchParams, paramsVO?: IParamsVO ): Promise<unknown> {
		const url = this.#buildNextItemApiUrl( params, paramsVO );
		this.#logger.log( '[NextItem]', 'fetchNextItemRecommendations', { url, params } );
		try {
			const response = await fetch( url, { method: 'GET' } );
			if ( !response.ok ) {
				this.#logger.warn( '[NextItem]', 'next-item request failed', { status: response.status, url } );
				throw new Error( `Next-item request failed: ${response.status}` );
			}
			return response.json() as unknown;
		} catch ( error ) {
			if ( error instanceof Error && error.message.startsWith( 'Next-item request failed' ) ) {
				throw error;
			}
			this.#logger.warn( '[NextItem]', 'fetchNextItemRecommendations error', {
				error: ( error as Error )?.message ?? error,
				url
			} );
			throw error;
		}
	}

	#buildNextItemApiUrl( params: INextItemFetchParams, paramsVO?: IParamsVO ): string {
		const baseUrl = this.#getNextItemServer( paramsVO );
		const searchParams = new URLSearchParams();

		searchParams.set( 'endCardId', params.endCardId );
		searchParams.set( 'contentType', params.contentType );

		if ( params._clientRegion ) {
			searchParams.set( '_clientRegion', params._clientRegion );
		}
		if ( params.channelSlug ) {
			searchParams.set( 'channelSlug', params.channelSlug );
		}
		if ( params.device ) {
			searchParams.set( 'device', params.device );
		}
		if ( params.start !== undefined ) {
			searchParams.set( 'start', String( params.start ) );
		}
		if ( params.rows !== undefined ) {
			searchParams.set( 'rows', String( params.rows ) );
		}
		if ( params.contentId ) {
			searchParams.set( 'contentId', params.contentId );
		}
		if ( params.episodeId ) {
			searchParams.set( 'episodeId', params.episodeId );
		}
		if ( params.showId ) {
			searchParams.set( 'showId', params.showId );
		}

		return `${baseUrl}${PLUTO_LEGACY_API_PATHS.nextItem}?${searchParams.toString()}`;
	}

	#makeRequest( url: string, paramsVO: IParamsVO ): Promise<Response> {
		const options: RequestInit = {
			method: 'GET',
			headers: {
				'Authorization': `Bearer ${paramsVO?.STITCHER?.token}`,
				'Content-Type': 'application/json'
			}
		};
		return fetch( url, options );
	}

	async addToMyList( _params: IUserListMutationParams ): Promise<unknown> {
		throw new Error( '[PlutoLegacyAdapter] converged My List mutations are not supported on the legacy adapter' );
	}

	async removeFromMyList( _params: IUserListMutationParams ): Promise<unknown> {
		throw new Error( '[PlutoLegacyAdapter] converged My List mutations are not supported on the legacy adapter' );
	}

	async addToFavorites( _params: IUserListMutationParams ): Promise<unknown> {
		throw new Error( '[PlutoLegacyAdapter] converged Favorites mutations are not supported on the legacy adapter' );
	}

	async removeFromFavorites( _params: IUserListMutationParams ): Promise<unknown> {
		throw new Error( '[PlutoLegacyAdapter] converged Favorites mutations are not supported on the legacy adapter' );
	}
}